/**
 * Save & Start for a validated draft.
 * Confirm, materialise, then activate or replace.
 * Preview generation must not call this.
 * Finishing the current plan first has no future-activation path:
 * this function refuses before it confirms anything.
 */
import {
  activateTrainingPlanMaterialisation,
  confirmTrainingPlanDraft,
  fetchCurrentTrainingBlock,
  materialiseTrainingPlanConfirmation,
  replaceActiveTrainingPlan,
} from "@/api/trainingPlanApi";
import { notifyActivePlanChanged } from "@/lib/activePlanSync";
import {
  QUICK_PLAN_LIFECYCLE_STAGE,
  QuickPlanLifecycleError,
} from "@/lib/quickPlanUpdate";

const wrapFailure = (stage, err) => {
  if (err instanceof QuickPlanLifecycleError) throw err;
  const message = err?.planError?.message || err?.message || "Could not start this training plan.";
  throw new QuickPlanLifecycleError(stage, message, { cause: err });
};

export const FUTURE_PLAN_NOT_SUPPORTED = "future_plan_not_supported";

export const futurePlanBlockerMessage = () => (
  "This preview is dated to start after your current plan. "
  + "Endurava cannot keep a second plan waiting, so it has not been scheduled. "
  + "Choose “Replace my current plan” if you want this preview to become your plan."
);

export const startValidatedTrainingPlan = async ({
  draftResponse,
  activePlanConflictChoice = null,
  requestIds,
  confirmDraft = confirmTrainingPlanDraft,
  materialiseConfirmation = materialiseTrainingPlanConfirmation,
  activateMaterialisation = activateTrainingPlanMaterialisation,
  replaceActivePlan = replaceActiveTrainingPlan,
  loadCurrentBlock = fetchCurrentTrainingBlock,
} = {}) => {
  const draftRef = draftResponse?.draft_ref;
  if (!draftRef) {
    throw new QuickPlanLifecycleError(
      QUICK_PLAN_LIFECYCLE_STAGE.GENERATION,
      "Plan generation did not return a draft reference.",
    );
  }
  if (!requestIds?.confirm || !requestIds?.materialise || !requestIds?.activate) {
    throw new Error("A stable request id is required before starting a plan.");
  }

  let before = null;
  try {
    before = await loadCurrentBlock();
  } catch (_err) {
    before = null;
  }

  if (before?.block_id && activePlanConflictChoice === "FINISH_CURRENT_PLAN_FIRST") {
    const err = new Error(futurePlanBlockerMessage());
    err.code = FUTURE_PLAN_NOT_SUPPORTED;
    throw err;
  }
  if (before?.block_id && activePlanConflictChoice !== "REPLACE_CURRENT_PLAN") {
    const err = new Error(
      "Choose whether to replace your current plan or finish it first.",
    );
    err.code = "plan_conflict_choice_required";
    throw err;
  }

  let confirmation;
  try {
    confirmation = await confirmDraft(draftRef, {
      clientRequestId: requestIds.confirm,
      expectedCandidateId: draftResponse?.draft?.candidate_id,
    });
  } catch (err) {
    wrapFailure(QUICK_PLAN_LIFECYCLE_STAGE.CONFIRMATION, err);
  }
  if (!confirmation?.confirmation_ref) {
    throw new QuickPlanLifecycleError(
      QUICK_PLAN_LIFECYCLE_STAGE.CONFIRMATION,
      "Confirmation did not return a reference.",
    );
  }

  let materialisation;
  try {
    materialisation = await materialiseConfirmation(confirmation.confirmation_ref, {
      clientRequestId: requestIds.materialise,
    });
  } catch (err) {
    wrapFailure(QUICK_PLAN_LIFECYCLE_STAGE.MATERIALISATION, err);
  }
  if (!materialisation?.materialisation_ref) {
    throw new QuickPlanLifecycleError(
      QUICK_PLAN_LIFECYCLE_STAGE.MATERIALISATION,
      "Materialisation did not return a reference.",
    );
  }

  let latest = before;
  try {
    latest = await loadCurrentBlock();
  } catch (_err) {
    latest = before;
  }
  if (!before?.block_id && latest?.block_id) {
    throw new QuickPlanLifecycleError(
      QUICK_PLAN_LIFECYCLE_STAGE.ACTIVATION,
      "A training plan became active while this preview was being prepared. Your workouts were not replaced.",
    );
  }
  if (before?.block_id && latest?.block_id && before.block_id !== latest.block_id) {
    throw new QuickPlanLifecycleError(
      QUICK_PLAN_LIFECYCLE_STAGE.REPLACEMENT,
      "Your current plan changed while this one was being prepared. Review it again before replacing it. Your active workouts were not replaced.",
    );
  }

  const shouldReplace = Boolean(latest?.block_id);
  let activation;
  try {
    if (shouldReplace) {
      activation = await replaceActivePlan(materialisation.materialisation_ref, {
        clientRequestId: requestIds.activate,
        expectedCurrentBlockId: latest.block_id,
      });
    } else {
      activation = await activateMaterialisation(materialisation.materialisation_ref, {
        clientRequestId: requestIds.activate,
      });
    }
  } catch (err) {
    wrapFailure(
      shouldReplace
        ? QUICK_PLAN_LIFECYCLE_STAGE.REPLACEMENT
        : QUICK_PLAN_LIFECYCLE_STAGE.ACTIVATION,
      err,
    );
  }

  notifyActivePlanChanged({
    source: "training-plan-creator",
    replaced: shouldReplace,
  });

  return {
    draft: draftResponse,
    confirmation,
    materialisation,
    activation,
    replaced: shouldReplace,
    scheduledLater: false,
    completed: true,
  };
};
